import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import apiClient from '../../services/apiClient';
import {
  DollarSign, CreditCard, Smartphone, Landmark, Banknote, Send, Clipboard,
  Lightbulb, BarChart3, AlertTriangle, Users, CheckCircle2, Loader2, Download,
  TrendingUp, FileText, Megaphone, Search, Pencil, Trash2, Copy, PartyPopper,
  Save, NotebookPen, Clock, Pause, Play
} from 'lucide-react';
import {
  exportToCSV,
  exportToJSON,
  exportToExcel,
  exportToPDF,
  formatForExport,
  getExportFilename
} from '../../utils/exportUtils';
import {
  getGivingCampaigns,
  getGivingCampaign,
  getCampaignDonations,
  createGivingCampaign,
  updateGivingCampaign,
  deleteGivingCampaign,
  toggleGivingCampaign,
  duplicateGivingCampaign,
  getGivingCampaignStats,
  sendCampaignMessage,
  addCampaignDonation,
  confirmCampaignDonation,
  clearApiMessage,
  clearError,
  clearGivingCampaignState,
  selectGivingCampaigns,
  selectGivingCampaignPagination,
  selectCurrentGivingCampaign,
  selectGivingCampaignStats,
  selectGivingCampaignsLoading,
  selectGivingCampaignsSubmitting,
  selectGivingCampaignsApiMessage,
  selectGivingCampaignsError,
} from '../../store/slices/growth/givingCampaignSlice';
import { selectCurrentUser } from '../../store/slices/authSlice';

const GivingCampaigns = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('mobile_money');
  const [paymentDetails, setPaymentDetails] = useState({});

  // ==================== SELECTORS ====================
  const campaigns = useSelector(selectGivingCampaigns) || [];
  const pagination = useSelector(selectGivingCampaignPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const currentCampaign = useSelector(selectCurrentGivingCampaign);
  const stats = useSelector(selectGivingCampaignStats);
  const loading = useSelector(selectGivingCampaignsLoading);
  const isSubmittingRedux = useSelector(selectGivingCampaignsSubmitting);
  const apiMessage = useSelector(selectGivingCampaignsApiMessage);
  const error = useSelector(selectGivingCampaignsError);

  // ==================== USER SELECTOR ====================
  const user = useSelector(selectCurrentUser);
  const organizationName = user?.organization || '';

  // ==================== REFS ====================
  const editNameRef = useRef(null);
  const editGoalRef = useRef(null);
  const editEndDateRef = useRef(null);
  const editKeywordRef = useRef(null);
  const editMessageRef = useRef(null);
  const editDescriptionRef = useRef(null);

  const editBankNameRef = useRef(null);
  const editBankAccountRef = useRef(null);
  const editBankAccountNameRef = useRef(null);
  const editMomoNumberRef = useRef(null);
  const editMomoProviderRef = useRef(null);
  const editMomoNameRef = useRef(null);

  const donorNameRef = useRef(null);
  const donorEmailRef = useRef(null);
  const donorPhoneRef = useRef(null);
  const amountRef = useRef(null);
  const paymentTypeRef = useRef(null);
  const paymentReferenceRef = useRef(null);
  const notesRef = useRef(null);
  const confirmPaymentRef = useRef(null);

  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== HELPERS ====================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    return `GH₵ ${Number(amount).toLocaleString()}`;
  };

  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== EFFECTS ====================
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getGivingCampaigns({
          per_page: itemsPerPage,
          page: currentPage,
          search: searchTerm,
          status: statusFilter
        }));
        await dispatch(getGivingCampaignStats());
      } catch (err) {
        console.error('Error loading campaigns:', err);
      }
    };

    loadData();

    return () => {
      dispatch(clearGivingCampaignState());
    };
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (statusFilter) params.status = statusFilter;

      dispatch(getGivingCampaigns(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  useEffect(() => {
    if (!apiMessage || !showToast) return;

    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (lastToastRef.current.message === messageKey &&
      (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }

    lastToastRef.current = { message: messageKey, timestamp: now };

    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== PAGINATION ====================
  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) setCurrentPage(currentPageNum - 1);
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) setCurrentPage(currentPageNum + 1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== CREATE CAMPAIGN ====================
  const openCreateCampaign = () => {
    const CreateModalContent = () => {
      const [name, setName] = useState('');
      const [description, setDescription] = useState('');
      const [goal, setGoal] = useState('');
      const [endDate, setEndDate] = useState('');
      const [smsKeyword, setSmsKeyword] = useState('');
      const [launchMessage, setLaunchMessage] = useState(
        `Dear {FirstName}, we are excited to launch our {CampaignName}! Our goal is GH₵ {Goal}.\n\n📱 Mobile Money: MTN - 0244123456 (${organizationName})\n🏦 Bank Transfer: GCB - 0123456789 (${organizationName})\n💵 Cash: In-person at church office\n\nYour generosity builds God's kingdom! 🙏 — ${organizationName}`
      );

      const [bankName, setBankName] = useState('');
      const [bankAccountNumber, setBankAccountNumber] = useState('');
      const [bankAccountName, setBankAccountName] = useState('');
      const [momoNumber, setMomoNumber] = useState('');
      const [momoProvider, setMomoProvider] = useState('MTN');
      const [momoName, setMomoName] = useState('');

      return (
        <div>
          <div className="modal-header">
            <div className="modal-title"><DollarSign size={16} style={{ verticalAlign: '-3px' }} /> New Giving Campaign</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Campaign Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" placeholder="Building Fund Drive" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-textarea" rows="2" placeholder="Brief description" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Goal Amount (GH₵) <span style={{ color: 'var(--red)' }}>*</span></label>
                <input className="form-input" type="number" placeholder="50000" value={goal} onChange={(e) => setGoal(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input className="form-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>

            <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '14px', marginTop: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)', marginBottom: '12px' }}>
                <CreditCard size={14} style={{ verticalAlign: '-2px' }} /> Payment Methods
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '10px' }}>
                <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '8px' }}><Smartphone size={14} style={{ verticalAlign: '-2px' }} /> Mobile Money</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Provider</label>
                    <select className="form-select" value={momoProvider} onChange={(e) => setMomoProvider(e.target.value)}>
                      <option value="MTN">MTN</option>
                      <option value="Vodafone">Vodafone</option>
                      <option value="AirtelTigo">AirtelTigo</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input className="form-input" placeholder="0244123456" value={momoNumber} onChange={(e) => setMomoNumber(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Account Name</label>
                  <input className="form-input" placeholder={organizationName} value={momoName} onChange={(e) => setMomoName(e.target.value)} />
                </div>
              </div>

              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '10px' }}>
                <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '8px' }}><Landmark size={14} style={{ verticalAlign: '-2px' }} /> Bank Transfer</div>
                <div className="form-group">
                  <label className="form-label">Bank Name</label>
                  <input className="form-input" placeholder="Ghana Commercial Bank" value={bankName} onChange={(e) => setBankName(e.target.value)} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Account Number</label>
                    <input className="form-input" placeholder="0123456789" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Account Name</label>
                    <input className="form-input" placeholder={organizationName} value={bankAccountName} onChange={(e) => setBankAccountName(e.target.value)} />
                  </div>
                </div>
              </div>

              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px' }}>
                <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '8px' }}><Banknote size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Cash</div>
                <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>In-person at church office</div>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '12px' }}>
              <label className="form-label">Giving SMS Keyword</label>
              <input className="form-input" placeholder="GIVE, BUILD, SUPPORT…" value={smsKeyword} onChange={(e) => setSmsKeyword(e.target.value)} />
              <div className="form-hint">Members text this to get the payment details</div>
            </div>
            <div className="form-group">
              <label className="form-label">Launch Message</label>
              <textarea className="form-textarea" rows="5" value={launchMessage} onChange={(e) => setLaunchMessage(e.target.value)} />
              <div className="form-hint">Use {'{FirstName}'}, {'{CampaignName}'}, {'{Goal}'} - {'{FirstName}'} will be replaced with each member's actual name</div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
            <button
              className="btn btn-gold btn-sm"
              disabled={isSubmittingRedux || !name || !goal}
              onClick={async () => {
                if (!name) { showToastMessage('Please enter a campaign name', 'error'); return; }
                if (!goal || parseFloat(goal) <= 0) { showToastMessage('Please enter a valid goal amount', 'error'); return; }

                setIsSubmitting(true);
                try {
                  const result = await dispatch(createGivingCampaign({
                    name,
                    description,
                    goal: parseFloat(goal),
                    end_date: endDate || null,
                    sms_keyword: smsKeyword || null,
                    launch_message: launchMessage,
                    bank_name: bankName || null,
                    bank_account_number: bankAccountNumber || null,
                    bank_account_name: bankAccountName || null,
                    mobile_money_number: momoNumber || null,
                    mobile_money_provider: momoProvider || null,
                    mobile_money_name: momoName || null,
                    status: 'Draft'
                  })).unwrap();

                  if (result.success) {
                    closeModal();
                    await dispatch(getGivingCampaigns({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter }));
                    await dispatch(getGivingCampaignStats());
                    showToastMessage(`💰 "${name}" created!`, 'success');
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to create campaign', 'error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isSubmittingRedux || isSubmitting ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </div>
      );
    };
    openModal(<CreateModalContent />);
  };

  // ==================== EDIT CAMPAIGN ====================
  const handleEditCampaign = (campaign) => {
    setEditingCampaignId(campaign.id);
    setTimeout(() => {
      if (editNameRef.current) editNameRef.current.value = campaign.name || '';
      if (editDescriptionRef.current) editDescriptionRef.current.value = campaign.description || '';
      if (editGoalRef.current) editGoalRef.current.value = campaign.goal || '';
      if (editEndDateRef.current) editEndDateRef.current.value = campaign.end_date || '';
      if (editKeywordRef.current) editKeywordRef.current.value = campaign.sms_keyword || '';
      if (editMessageRef.current) editMessageRef.current.value = campaign.launch_message || '';
      if (editBankNameRef.current) editBankNameRef.current.value = campaign.bank_name || '';
      if (editBankAccountRef.current) editBankAccountRef.current.value = campaign.bank_account_number || '';
      if (editBankAccountNameRef.current) editBankAccountNameRef.current.value = campaign.bank_account_name || '';
      if (editMomoNumberRef.current) editMomoNumberRef.current.value = campaign.mobile_money_number || '';
      if (editMomoProviderRef.current) editMomoProviderRef.current.value = campaign.mobile_money_provider || 'MTN';
      if (editMomoNameRef.current) editMomoNameRef.current.value = campaign.mobile_money_name || '';
    }, 50);

    const EditModalContent = () => {
      const [name, setName] = useState(campaign.name || '');
      const [description, setDescription] = useState(campaign.description || '');
      const [goal, setGoal] = useState(campaign.goal || '');
      const [endDate, setEndDate] = useState(campaign.end_date || '');
      const [smsKeyword, setSmsKeyword] = useState(campaign.sms_keyword || '');
      const [launchMessage, setLaunchMessage] = useState(campaign.launch_message || '');
      const [bankName, setBankName] = useState(campaign.bank_name || '');
      const [bankAccountNumber, setBankAccountNumber] = useState(campaign.bank_account_number || '');
      const [bankAccountName, setBankAccountName] = useState(campaign.bank_account_name || '');
      const [momoNumber, setMomoNumber] = useState(campaign.mobile_money_number || '');
      const [momoProvider, setMomoProvider] = useState(campaign.mobile_money_provider || 'MTN');
      const [momoName, setMomoName] = useState(campaign.mobile_money_name || '');

      return (
        <div>
          <div className="modal-header">
            <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Campaign: {campaign.name}</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Campaign Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" ref={editNameRef} defaultValue={campaign.name || ''} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-textarea" rows="2" ref={editDescriptionRef} defaultValue={campaign.description || ''} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Goal Amount (GH₵) <span style={{ color: 'var(--red)' }}>*</span></label>
                <input className="form-input" type="number" ref={editGoalRef} defaultValue={campaign.goal || ''} onChange={(e) => setGoal(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input className="form-input" type="date" ref={editEndDateRef} defaultValue={campaign.end_date || ''} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>

            <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '14px', marginTop: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)', marginBottom: '12px' }}>
                <CreditCard size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Payment Methods
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '10px' }}>
                <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '8px' }}><Smartphone size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Mobile Money</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Provider</label>
                    <select className="form-select" ref={editMomoProviderRef} defaultValue={campaign.mobile_money_provider || 'MTN'} onChange={(e) => setMomoProvider(e.target.value)}>
                      <option value="MTN">MTN</option>
                      <option value="Vodafone">Vodafone</option>
                      <option value="AirtelTigo">AirtelTigo</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input className="form-input" ref={editMomoNumberRef} defaultValue={campaign.mobile_money_number || ''} placeholder="0244123456" onChange={(e) => setMomoNumber(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Account Name</label>
                  <input className="form-input" ref={editMomoNameRef} defaultValue={campaign.mobile_money_name || ''} placeholder={organizationName} onChange={(e) => setMomoName(e.target.value)} />
                </div>
              </div>

              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '10px' }}>
                <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '8px' }}><Landmark size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Bank Transfer</div>
                <div className="form-group">
                  <label className="form-label">Bank Name</label>
                  <input className="form-input" ref={editBankNameRef} defaultValue={campaign.bank_name || ''} placeholder="Ghana Commercial Bank" onChange={(e) => setBankName(e.target.value)} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Account Number</label>
                    <input className="form-input" ref={editBankAccountRef} defaultValue={campaign.bank_account_number || ''} placeholder="0123456789" onChange={(e) => setBankAccountNumber(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Account Name</label>
                    <input className="form-input" ref={editBankAccountNameRef} defaultValue={campaign.bank_account_name || ''} placeholder={organizationName} onChange={(e) => setBankAccountName(e.target.value)} />
                  </div>
                </div>
              </div>

              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px' }}>
                <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '8px' }}><Banknote size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Cash</div>
                <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>In-person at church office</div>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '12px' }}>
              <label className="form-label">Giving SMS Keyword</label>
              <input className="form-input" ref={editKeywordRef} defaultValue={campaign.sms_keyword || ''} onChange={(e) => setSmsKeyword(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Launch Message</label>
              <textarea className="form-textarea" rows="5" ref={editMessageRef} defaultValue={campaign.launch_message || ''} onChange={(e) => setLaunchMessage(e.target.value)} />
              <div className="form-hint">Use {'{FirstName}'}, {'{CampaignName}'}, {'{Goal}'} - {'{FirstName}'} will be replaced with each member's actual name</div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
            <button
              className="btn btn-gold btn-sm"
              disabled={isSubmittingRedux || !name || !goal}
              onClick={async () => {
                if (!name) { showToastMessage('Please enter a campaign name', 'error'); return; }
                if (!goal || parseFloat(goal) <= 0) { showToastMessage('Please enter a valid goal amount', 'error'); return; }

                setIsSubmitting(true);
                try {
                  const result = await dispatch(updateGivingCampaign({
                    id: campaign.id,
                    data: {
                      name,
                      description,
                      goal: parseFloat(goal),
                      end_date: endDate || null,
                      sms_keyword: smsKeyword || null,
                      launch_message: launchMessage,
                      bank_name: bankName || null,
                      bank_account_number: bankAccountNumber || null,
                      bank_account_name: bankAccountName || null,
                      mobile_money_number: momoNumber || null,
                      mobile_money_provider: momoProvider || null,
                      mobile_money_name: momoName || null
                    }
                  })).unwrap();

                  if (result.success) {
                    closeModal();
                    await dispatch(getGivingCampaigns({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter }));
                    await dispatch(getGivingCampaignStats());
                    showToastMessage(`✅ "${name}" updated successfully!`, 'success');
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to update campaign', 'error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isSubmittingRedux || isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      );
    };
    openModal(<EditModalContent />);
  };

  // ==================== DELETE CAMPAIGN ====================
  const handleDeleteCampaign = (campaign) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete Campaign</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>Are you sure you want to delete <strong>{campaign.name}</strong>?</p>
          <div style={{ padding: '12px', background: 'var(--red-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--red)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will permanently remove the campaign and all donor data.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-danger btn-sm"
            disabled={isSubmittingRedux || isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteGivingCampaign(campaign.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getGivingCampaigns({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter }));
                  await dispatch(getGivingCampaignStats());
                  showToastMessage(`🗑️ "${campaign.name}" deleted`, 'error');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete campaign', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Delete Campaign
          </button>
        </div>
      </div>
    );
  };

  // ==================== TOGGLE STATUS ====================
  const toggleCampaign = async (campaign) => {
    try {
      const result = await dispatch(toggleGivingCampaign(campaign.id)).unwrap();
      if (result.success) {
        await dispatch(getGivingCampaigns({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter }));
        await dispatch(getGivingCampaignStats());
        showToastMessage(`✅ Campaign ${result.status || 'toggled'}`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to toggle campaign', 'error');
    }
  };

  // ==================== DUPLICATE ====================
  const handleDuplicateCampaign = async (campaign) => {
    setIsSubmitting(true);
    try {
      const result = await dispatch(duplicateGivingCampaign(campaign.id)).unwrap();
      if (result.success) {
        await dispatch(getGivingCampaigns({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter }));
        await dispatch(getGivingCampaignStats());
        showToastMessage(`📋 "${result.campaign.name}" created`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to duplicate campaign', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== ADD DONATION ====================
  const openAddDonationModal = (campaign) => {
    const paymentMethods = campaign.available_payment_methods || [{ type: 'cash', label: 'Cash' }];

    const AddDonationModalContent = () => {
      const [selectedMethod, setSelectedMethod] = useState(paymentMethods[0]?.type || 'cash');
      const [methodDetails, setMethodDetails] = useState(paymentMethods[0] || {});
      const [isConfirmed, setIsConfirmed] = useState(true);

      return (
        <div>
          <div className="modal-header">
            <div className="modal-title"><CreditCard size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Add Donation: {campaign.name}</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Donor Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" ref={donorNameRef} placeholder="John Asante" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" ref={donorEmailRef} placeholder="john@example.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" ref={donorPhoneRef} placeholder="+233241234567" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Amount (GH₵) <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" type="number" ref={amountRef} placeholder="500" />
            </div>
            <div className="form-group">
              <label className="form-label">Payment Method <span style={{ color: 'var(--red)' }}>*</span></label>
              <select className="form-select" ref={paymentTypeRef} onChange={(e) => {
                const method = paymentMethods.find(m => m.type === e.target.value);
                setSelectedMethod(e.target.value);
                setMethodDetails(method || {});
              }}>
                {paymentMethods.map((method, idx) => (
                  <option key={idx} value={method.type}>{method.label}{method.provider && ` - ${method.provider}`}{method.number && ` (${method.number})`}</option>
                ))}
              </select>
            </div>
            {methodDetails && (
              <div style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--navy)', marginBottom: '6px' }}>
                  <Clipboard size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Payment Instructions
                </div>
                {methodDetails.type === 'mobile_money' && (
                  <>
                    <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>Send to: <strong>{methodDetails.provider}</strong> - {methodDetails.number}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>Account Name: <strong>{methodDetails.name || organizationName}</strong></div>
                  </>
                )}
                {methodDetails.type === 'bank_transfer' && (
                  <>
                    <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>Bank: <strong>{methodDetails.bank_name}</strong></div>
                    <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>Account: <strong>{methodDetails.account_number}</strong></div>
                    <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>Account Name: <strong>{methodDetails.account_name || organizationName}</strong></div>
                  </>
                )}
                {methodDetails.type === 'cash' && (
                  <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>Please give cash at the church office.</div>
                )}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Payment Reference</label>
              <input className="form-input" ref={paymentReferenceRef} placeholder="Enter transaction ID or reference number" />
              <div className="form-hint">Optional: Reference number from the payment</div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" rows="2" ref={notesRef} placeholder="Any additional notes..." />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--cream)', borderRadius: '8px' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>Mark as Confirmed</div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Payment has been received and verified</div>
              </div>
              <label className="toggle">
                <input type="checkbox" ref={confirmPaymentRef} defaultChecked={true} onChange={(e) => setIsConfirmed(e.target.checked)} />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
            <button
              className="btn btn-gold btn-sm"
              disabled={isSubmittingRedux || isSubmitting}
              onClick={async () => {
                const name = donorNameRef.current?.value?.trim();
                const amount = parseFloat(amountRef.current?.value);
                const isConfirmed = confirmPaymentRef.current?.checked || false;

                if (!name) { showToastMessage('Please enter donor name', 'error'); return; }
                if (!amount || amount <= 0) { showToastMessage('Please enter a valid amount', 'error'); return; }
                if (!selectedMethod) { showToastMessage('Please select a payment method', 'error'); return; }

                setIsSubmitting(true);
                try {
                  const result = await dispatch(addCampaignDonation({
                    id: campaign.id,
                    data: {
                      donor_name: name,
                      donor_email: donorEmailRef.current?.value?.trim() || null,
                      donor_phone: donorPhoneRef.current?.value?.trim() || null,
                      amount: amount,
                      payment_method: selectedMethod,
                      payment_type: selectedMethod,
                      payment_reference: paymentReferenceRef.current?.value?.trim() || null,
                      status: isConfirmed ? 'completed' : 'pending',
                      notes: notesRef.current?.value?.trim() || null
                    }
                  })).unwrap();

                  if (result.success) {
                    closeModal();
                    const statusText = isConfirmed ? 'confirmed' : 'pending confirmation';
                    showToastMessage(`✅ Donation of ${formatCurrency(amount)} added for ${name} (${statusText})`, 'success');
                    await dispatch(getGivingCampaigns({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter }));
                    await dispatch(getGivingCampaignStats());
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to add donation', 'error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              Add Donation
            </button>
          </div>
        </div>
      );
    };
    openModal(<AddDonationModalContent />);
  };

  // ==================== SEND REMINDER ====================
  const sendReminder = (campaign) => {
    let paymentDetails = '';
    if (campaign.mobile_money_number) {
      paymentDetails += `\n📱 Mobile Money: ${campaign.mobile_money_provider || 'MTN'} - ${campaign.mobile_money_number}`;
      if (campaign.mobile_money_name) paymentDetails += ` (${campaign.mobile_money_name})`;
    }
    if (campaign.bank_account_number) {
      paymentDetails += `\n🏦 Bank Transfer: ${campaign.bank_name || 'Bank'}`;
      if (campaign.bank_branch) paymentDetails += ` (${campaign.bank_branch} Branch)`;
      paymentDetails += ` - Acc: ${campaign.bank_account_number}`;
      if (campaign.bank_account_name) paymentDetails += ` (${campaign.bank_account_name})`;
    }
    paymentDetails += `\n💵 Cash: In-person at church office`;

    const ReminderModalContent = () => {
      const [message, setMessage] = useState(
        `Hello {FirstName}! 🙏 Our ${campaign.name} campaign needs your support. We've raised ${formatCurrency(campaign.raised)} of our ${formatCurrency(campaign.goal)} goal.${paymentDetails}\n\nEvery gift counts! — ${organizationName}`
      );
      const previewMessage = message.replace(/{FirstName}/g, 'Emmanuel').replace(/{OrganizationName}/g, organizationName);

      return (
        <div>
          <div className="modal-header">
            <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Send Reminder: {campaign.name}</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>Send giving reminder to <strong>all members</strong> for <strong>{campaign.name}</strong></div>
              <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
                <Lightbulb size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{'{FirstName}'} will be replaced with each member's actual first name
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Message <span style={{ color: 'var(--red)' }}>*</span></label>
              <textarea className="form-textarea" rows="6" value={message} onChange={(e) => setMessage(e.target.value)} />
              <div className="form-hint">Use {'{FirstName}'} for personalisation - will be replaced with each member's actual name</div>
            </div>
            <div style={{ padding: '12px', background: 'var(--navy)', borderRadius: '8px', marginTop: '8px' }}>
              <div style={{ fontSize: '11px', color: 'var(--gold)', marginBottom: '4px', fontWeight: '600' }}>
                <Smartphone size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Preview (Sample: Emmanuel)
              </div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,.85)', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{previewMessage}</div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
            <button
              className="btn btn-gold btn-sm"
              disabled={isSubmittingRedux || isSubmitting || !message.trim()}
              onClick={async () => {
                if (!message.trim()) { showToastMessage('Please enter a message', 'error'); return; }
                setIsSubmitting(true);
                try {
                  const result = await dispatch(sendCampaignMessage({ id: campaign.id, type: 'reminder', message: message.trim() })).unwrap();
                  if (result.success) {
                    closeModal();
                    showToastMessage(`📨 Reminder sent for "${campaign.name}"!`, 'success');
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to send reminder', 'error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isSubmittingRedux || isSubmitting ? 'Sending...' : 'Send Reminder'}
            </button>
          </div>
        </div>
      );
    };
    openModal(<ReminderModalContent />);
  };

  // ==================== SEND PROGRESS UPDATE ====================
  const sendProgressUpdate = (campaign) => {
    const pct = Math.round((campaign.raised / campaign.goal) * 100);
    let paymentDetails = '';
    if (campaign.mobile_money_number) {
      paymentDetails += `\n📱 Mobile Money: ${campaign.mobile_money_provider || 'MTN'} - ${campaign.mobile_money_number}`;
    }
    if (campaign.bank_account_number) {
      paymentDetails += `\n🏦 Bank Transfer: ${campaign.bank_name || 'Bank'}`;
      if (campaign.bank_branch) paymentDetails += ` (${campaign.bank_branch} Branch)`;
      paymentDetails += ` - Acc: ${campaign.bank_account_number}`;
    }
    paymentDetails += `\n💵 Cash: In-person at church office`;

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><BarChart3 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Send Progress Update: {campaign.name}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Loader2 size={20} /></div>
            <div>Loading donors...</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );

    const fetchDonorsAndOpenModal = async () => {
      try {
        const result = await dispatch(getCampaignDonations({ id: campaign.id, params: { per_page: 100, status: 'completed' } })).unwrap();
        closeModal();
        const donations = result.donations || [];
        const validDonors = donations.filter(d => d.donor_name);
        if (validDonors.length === 0) {
          showToastMessage('No donors found for this campaign', 'error');
          return;
        }
        const donorsWithPhone = validDonors.filter(d => d.donor_phone);
        const donorNames = [...new Set(validDonors.map(d => d.donor_name))];
        const sampleDonorName = donorNames.length > 0 ? donorNames[0] : 'Emmanuel';

        const ProgressModalContent = () => {
          const [message, setMessage] = useState(
            `Dear {FirstName}, thank you for your generosity! 🙏 We've raised ${formatCurrency(campaign.raised)} (${pct}%) of our ${formatCurrency(campaign.goal)} goal for ${campaign.name}.${paymentDetails}\n\nYour support is making a difference! — ${organizationName}`
          );
          const previewMessage = message.replace(/{FirstName}/g, sampleDonorName).replace(/{OrganizationName}/g, organizationName);

          return (
            <div>
              <div className="modal-header">
                <div className="modal-title"><BarChart3 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Send Progress Update: {campaign.name}</div>
                <button className="modal-close" onClick={closeModal}>×</button>
              </div>
              <div className="modal-body">
                <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>Send progress update to <strong>{validDonors.length} donors</strong></div>
                  {validDonors.length - donorsWithPhone.length > 0 && (
                    <div style={{ marginTop: '8px', padding: '8px 12px', background: 'var(--amber-bg)', borderRadius: '6px', fontSize: '12px', color: 'var(--amber)' }}>
                      <AlertTriangle size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{validDonors.length - donorsWithPhone.length} donor(s) don't have phone numbers and will not receive SMS.
                      {donorsWithPhone.length > 0 && ` ${donorsWithPhone.length} donor(s) will receive the update.`}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
                    <Lightbulb size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{'{FirstName}'} will be replaced with each donor's name from the donation records
                  </div>
                </div>
                {donorNames.length > 0 && (
                  <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px', maxHeight: '100px', overflowY: 'auto' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '4px' }}><Users size={11} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Donors will receive this message:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {donorNames.slice(0, 5).map((name, idx) => (
                        <span key={idx} style={{ fontSize: '11px', background: 'var(--white)', padding: '2px 10px', borderRadius: '12px', border: '1px solid var(--border)' }}>{name}</span>
                      ))}
                      {donorNames.length > 5 && <span style={{ fontSize: '11px', color: 'var(--text-light)' }}>+{donorNames.length - 5} more</span>}
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Update Message</label>
                  <textarea className="form-textarea" rows="6" value={message} onChange={(e) => setMessage(e.target.value)} />
                  <div className="form-hint">Use {'{FirstName}'} for personalisation - will be replaced with each donor's name from the donation records</div>
                </div>
                <div style={{ padding: '12px', background: 'var(--navy)', borderRadius: '8px', marginTop: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--gold)', marginBottom: '4px', fontWeight: '600' }}>
                    <Smartphone size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Preview (Sample Donor: {sampleDonorName})
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,.85)', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{previewMessage}</div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
                <button
                  className="btn btn-gold btn-sm"
                  disabled={isSubmittingRedux || isSubmitting || !message.trim() || donorsWithPhone.length === 0}
                  onClick={async () => {
                    if (!message.trim()) { showToastMessage('Please enter a message', 'error'); return; }
                    if (donorsWithPhone.length === 0) { showToastMessage('No donors have phone numbers to receive SMS', 'error'); return; }
                    setIsSubmitting(true);
                    try {
                      const result = await dispatch(sendCampaignMessage({ id: campaign.id, type: 'progress', message: message.trim() })).unwrap();
                      if (result.success) {
                        closeModal();
                        showToastMessage(`📊 Progress update sent to ${donorsWithPhone.length} donors!`, 'success');
                      }
                    } catch (err) {
                      showToastMessage(err.message || 'Failed to send progress update', 'error');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                >
                  {isSubmittingRedux || isSubmitting ? 'Sending...' : `Send Update (${donorsWithPhone.length})`}
                </button>
              </div>
            </div>
          );
        };
        openModal(<ProgressModalContent />);
      } catch (err) {
        console.error('Error fetching donors:', err);
        closeModal();
        showToastMessage(err.message || 'Failed to load donors', 'error');
      }
    };
    fetchDonorsAndOpenModal();
  };

  // ==================== VIEW DONORS ====================
  const viewDonors = (campaign) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Users size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Donors: {campaign.name}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Loader2 size={20} /></div>
            <div>Loading donors...</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );

    const fetchDonors = async () => {
      try {
        const result = await dispatch(getCampaignDonations({ id: campaign.id, params: { per_page: 50 } })).unwrap();
        closeModal();
        const donations = result.donations || [];

        openModal(
          <div>
            <div className="modal-header">
              <div className="modal-title"><Users size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Donors: {campaign.name}</div>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Total Donors</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{donations.length}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button className="btn btn-gold btn-sm" onClick={() => { closeModal(); openDonorsExportModal(campaign, donations); }} title="Export donor list">
                      <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export
                    </button>
                    <button className="btn btn-gold btn-sm" onClick={() => { closeModal(); sendProgressUpdate(campaign); }} title="Send progress update to donors">
                      <BarChart3 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Update
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {donations.length > 0 ? (
                  donations.map((donation, index) => {
                    const colors = ['#4f46e5', '#059669', '#d97706', '#7c3aed', '#0891b2', '#dc2626'];
                    const color = colors[index % colors.length];
                    const initials = donation.donor_name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
                    return (
                      <div key={donation.id || index} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
                        <div className="member-row-avatar" style={{ background: color, width: '28px', height: '28px', fontSize: '11px' }}>{initials}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', fontWeight: '500' }}>{donation.donor_name}</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>{donation.donor_email || donation.donor_phone || 'No contact info'} · Gave {formatCurrency(donation.amount)}{donation.donated_at && ` · ${formatDate(donation.donated_at)}`}</div>
                        </div>
                        <span className={`badge ${donation.status === 'completed' ? 'badge-green' : 'badge-amber'}`} style={{ fontSize: '10px' }}>
                          {donation.status === 'completed' ? <><CheckCircle2 size={11} style={{ verticalAlign: '-2px' }} /> Confirmed</> : <><Clock size={11} style={{ verticalAlign: '-2px' }} /> Pending</>}
                        </span>
                        {donation.status === 'pending' && (
                          <button className="btn btn-gold btn-sm" style={{ fontSize: '10px', padding: '2px 8px' }} onClick={() => confirmDonation(donation, campaign)}>Confirm</button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-light)' }}>No donors found for this campaign.</div>
                )}
              </div>
              {result.pagination && result.pagination.total > 50 && (
                <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '12px', color: 'var(--text-light)' }}>Showing 50 of {result.pagination.total} donors</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
            </div>
          </div>
        );
      } catch (err) {
        console.error('Error fetching donors:', err);
        closeModal();
        showToastMessage(err.message || 'Failed to load donors', 'error');
      }
    };
    fetchDonors();
  };

  // ==================== CONFIRM DONATION ====================
  const confirmDonation = (donation, campaign) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><CheckCircle2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Confirm Donation</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>Confirm donation from <strong>{donation.donor_name}</strong> for <strong>{formatCurrency(donation.amount)}</strong>?</p>
          <div style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Payment Method: <strong>{donation.payment_method}</strong></div>
            {donation.payment_reference && <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Reference: <strong>{donation.payment_reference}</strong></div>}
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Donated: <strong>{formatDate(donation.donated_at)}</strong></div>
          </div>
          <div style={{ padding: '12px', background: 'var(--green-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--green)' }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will mark the donation as confirmed and update the campaign totals.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            disabled={isSubmittingRedux || isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(confirmCampaignDonation({ campaignId: campaign.id, donationId: donation.id })).unwrap();
                if (result.success) {
                  closeModal();
                  showToastMessage(`✅ Donation confirmed for ${donation.donor_name}`, 'success');
                  await dispatch(getGivingCampaigns({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter }));
                  await dispatch(getGivingCampaignStats());
                  await dispatch(getCampaignDonations({ id: campaign.id, params: { per_page: 50 } }));
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to confirm donation', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Confirm Donation
          </button>
        </div>
      </div>
    );
  };

  // ==================== EXPORT FUNCTIONS ====================
  const openDonorsExportModal = (campaign, donations) => {
    if (!donations || donations.length === 0) {
      showToastMessage('No donors to export', 'error');
      return;
    }

    const data = donations.map(d => ({
      'Donor Name': d.donor_name,
      'Email': d.donor_email || 'N/A',
      'Phone': d.donor_phone || 'N/A',
      'Amount': formatCurrency(d.amount),
      'Date': formatDate(d.donated_at),
      'Status': d.status || 'Confirmed',
      'Payment Method': d.payment_method || 'N/A',
      'Reference': d.payment_reference || 'N/A'
    }));

    const fields = [
      { key: 'Donor Name', label: 'Donor Name' },
      { key: 'Email', label: 'Email' },
      { key: 'Phone', label: 'Phone' },
      { key: 'Amount', label: 'Amount' },
      { key: 'Date', label: 'Date' },
      { key: 'Status', label: 'Status' },
      { key: 'Payment Method', label: 'Payment Method' },
      { key: 'Reference', label: 'Reference' }
    ];

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Export Donors</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>Export donors for <strong>{campaign.name}</strong></div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>Select Format</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const formattedData = formatForExport(data, fields);
                    const filename = getExportFilename(`donors_${campaign.name.toLowerCase().replace(/\s/g, '_')}`);
                    switch (format) {
                      case 'CSV': exportToCSV(formattedData, filename); break;
                      case 'JSON': exportToJSON(formattedData, filename); break;
                      case 'Excel': exportToExcel(formattedData, filename); break;
                      case 'PDF': exportToPDF(formattedData, `Donors - ${campaign.name}`, filename); break;
                      default: showToastMessage('Export format not supported', 'error'); return;
                    }
                    showToastMessage(`📥 ${data.length} donors exported as ${format}!`, 'success');
                    closeModal();
                  }}
                  style={{ padding: '8px', flexDirection: 'column', alignItems: 'center', gap: '4px', height: 'auto', border: '2px solid var(--border)', borderRadius: '8px', transition: 'all 0.2s', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.background = 'var(--gold-pale)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ display: 'flex' }}>{format === 'CSV' && <BarChart3 size={20} />}{format === 'JSON' && <Clipboard size={20} />}{format === 'Excel' && <TrendingUp size={20} />}{format === 'PDF' && <FileText size={20} />}</span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>
          <div style={{ padding: '10px 12px', background: 'var(--cream)', borderRadius: '8px', fontSize: '12px', color: 'var(--text-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{data.length} donors will be exported</span>
            <span style={{ fontSize: '11px' }}>{campaign.name}</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  const openExportAllModal = () => {
    const data = campaigns.map(c => ({
      'Campaign Name': c.name,
      'Description': c.description || 'N/A',
      'Goal': formatCurrency(c.goal),
      'Raised': formatCurrency(c.raised),
      'Donors': c.donors,
      'Progress': `${c.progress_percentage || 0}%`,
      'Status': c.status,
      'End Date': formatDate(c.end_date)
    }));

    const fields = [
      { key: 'Campaign Name', label: 'Campaign Name' },
      { key: 'Description', label: 'Description' },
      { key: 'Goal', label: 'Goal' },
      { key: 'Raised', label: 'Raised' },
      { key: 'Donors', label: 'Donors' },
      { key: 'Progress', label: 'Progress' },
      { key: 'Status', label: 'Status' },
      { key: 'End Date', label: 'End Date' }
    ];

    openExportModal(data, fields, 'All Campaigns', 'all-campaigns');
  };

  const openExportCampaignModal = (campaign) => {
    const data = [{
      'Campaign Name': campaign.name,
      'Description': campaign.description || 'N/A',
      'Goal': formatCurrency(campaign.goal),
      'Raised': formatCurrency(campaign.raised),
      'Donors': campaign.donors,
      'Progress': `${campaign.progress_percentage || 0}%`,
      'Status': campaign.status,
      'End Date': formatDate(campaign.end_date),
      'Payment Methods': campaign.available_payment_methods?.map(m => m.label).join(', ') || 'Cash'
    }];

    const fields = [
      { key: 'Campaign Name', label: 'Campaign Name' },
      { key: 'Description', label: 'Description' },
      { key: 'Goal', label: 'Goal' },
      { key: 'Raised', label: 'Raised' },
      { key: 'Donors', label: 'Donors' },
      { key: 'Progress', label: 'Progress' },
      { key: 'Status', label: 'Status' },
      { key: 'End Date', label: 'End Date' },
      { key: 'Payment Methods', label: 'Payment Methods' }
    ];

    openExportModal(data, fields, campaign.name, `campaign-${campaign.id}`);
  };

  const openExportModal = (data, fields, title, filename) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Export: {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>Export {data.length} record{data.length !== 1 ? 's' : ''} in your preferred format.</div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>Select Format</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const formattedData = formatForExport(data, fields);
                    const file = getExportFilename(filename);
                    switch (format) {
                      case 'CSV': exportToCSV(formattedData, file); break;
                      case 'JSON': exportToJSON(formattedData, file); break;
                      case 'Excel': exportToExcel(formattedData, file); break;
                      case 'PDF': exportToPDF(formattedData, title, file); break;
                      default: showToastMessage('Export format not supported', 'error'); return;
                    }
                    showToastMessage(`📥 ${data.length} records exported as ${format}!`, 'success');
                    closeModal();
                  }}
                  style={{ padding: '8px', flexDirection: 'column', alignItems: 'center', gap: '4px', height: 'auto', border: '2px solid var(--border)', borderRadius: '8px', transition: 'all 0.2s', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.background = 'var(--gold-pale)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ display: 'flex' }}>{format === 'CSV' && <BarChart3 size={20} />}{format === 'JSON' && <Clipboard size={20} />}{format === 'Excel' && <TrendingUp size={20} />}{format === 'PDF' && <FileText size={20} />}</span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>
          <div style={{ padding: '10px 12px', background: 'var(--cream)', borderRadius: '8px', fontSize: '12px', color: 'var(--text-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{data.length} records will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================
  const statsData = stats || {
    total: 0,
    active: 0,
    completed: 0,
    draft: 0,
    total_raised: 0,
    total_donors: 0
  };

  // ==================== LOADING STATE (inline) ====================
  // The loading check is now inside the JSX below, not an early return.
  // We keep the code here but the actual conditional rendering happens in the campaigns list.

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <DollarSign size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Send giving reminders and campaign updates via SMS with payment details.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-gold btn-sm" onClick={openCreateCampaign} disabled={loading}>
            + New Campaign
          </button>
          <button className="btn btn-ghost btn-sm" onClick={openExportAllModal} disabled={loading || campaigns.length === 0}>
            <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export All
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Raised</div>
          <div className="stat-value">{formatCurrency(statsData.total_raised || 0)}</div>
          <div className="stat-change up">↑ all campaigns</div>
          <div className="stat-icon"><DollarSign size={20} /></div>
        </div>
        <div className="stat-card green" style={{ cursor: 'default' }}>
          <div className="stat-label">Active Campaigns</div>
          <div className="stat-value">{statsData.active || 0}</div>
          <div className="stat-change up">running now</div>
          <div className="stat-icon"><Megaphone size={20} /></div>
        </div>
        <div className="stat-card blue" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Donors</div>
          <div className="stat-value">{statsData.total_donors || 0}</div>
          <div className="stat-change up">↑ all campaigns</div>
          <div className="stat-icon"><Users size={20} /></div>
        </div>
        <div className="stat-card purple" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Campaigns</div>
          <div className="stat-value">{statsData.total || 0}</div>
          <div className="stat-change up">all time</div>
          <div className="stat-icon"><Clipboard size={20} /></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={16} /></span>
            <input type="text" placeholder="Search campaigns..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
          <select className="form-select" style={{ width: '150px' }} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} disabled={loading}>
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Completed">Completed</option>
            <option value="Draft">Draft</option>
            <option value="Paused">Paused</option>
          </select>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{campaigns.length} campaigns found</div>
        </div>
      </div>

      {/* Campaigns List - with inline loading */}
      {loading && campaigns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading campaigns...</div>
        </div>
      ) : campaigns.length > 0 ? (
        campaigns.map(c => {
          const pct = c.progress_percentage || 0;
          const remaining = c.goal - c.raised;
          const color = c.color || '#4f46e5';

          return (
            <div key={c.id} className="card" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--navy)', marginBottom: '4px' }}>{c.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    {c.end_date ? `Ends: ${formatDate(c.end_date)}` : 'No end date'} · {c.donors} donors
                  </div>
                  {c.description && <div style={{ fontSize: '12px', color: 'var(--text-mid)', marginTop: '4px' }}>{c.description}</div>}
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className={`badge ${c.status === 'Active' ? 'badge-green' : c.status === 'Completed' ? 'badge-navy' : c.status === 'Paused' ? 'badge-amber' : 'badge-amber'}`}>{c.status}</span>
                  {c.status !== 'Completed' && (
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => toggleCampaign(c)} title={c.status === 'Active' ? 'Pause campaign' : 'Activate campaign'} disabled={loading || isSubmitting}>
                      {c.status === 'Active' ? <Pause size={14} /> : <Play size={14} />}
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleEditCampaign(c)} title="Edit campaign" disabled={loading}><Pencil size={14} /></button>
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDeleteCampaign(c)} title="Delete campaign" style={{ color: 'var(--red)' }} disabled={loading}><Trash2 size={14} /></button>
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openExportCampaignModal(c)} title="Export campaign" disabled={loading}><Download size={14} /></button>
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-mid)', marginBottom: '4px' }}>
                  <CreditCard size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Payment Methods
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                  {c.mobile_money_number && (
                    <span style={{ fontSize: '11px', color: 'var(--text-mid)', background: 'var(--cream)', padding: '3px 12px', borderRadius: '12px', border: '1px solid var(--border)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Smartphone size={12} /> <span>{c.mobile_money_provider || 'MTN'}: {c.mobile_money_number}</span>
                      {c.mobile_money_name && <span style={{ color: 'var(--text-light)' }}>({c.mobile_money_name})</span>}
                    </span>
                  )}
                  {c.bank_account_number && (
                    <span style={{ fontSize: '11px', color: 'var(--text-mid)', background: 'var(--cream)', padding: '3px 12px', borderRadius: '12px', border: '1px solid var(--border)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Landmark size={12} /> <span>{c.bank_name || 'Bank'}: {c.bank_account_number}</span>
                      {c.bank_account_name && <span style={{ color: 'var(--text-light)' }}>({c.bank_account_name})</span>}
                    </span>
                  )}
                  <span style={{ fontSize: '11px', color: 'var(--text-mid)', background: 'var(--cream)', padding: '3px 12px', borderRadius: '12px', border: '1px solid var(--border)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Banknote size={12} /> <span>Cash: In-person at church</span>
                  </span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                <div style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Goal</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--text-mid)' }}>{formatCurrency(c.goal)}</div>
                </div>
                <div style={{ padding: '12px', background: 'var(--green-bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--green)' }}>Raised</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--green)' }}>{formatCurrency(c.raised)}</div>
                </div>
                <div style={{ padding: '12px', background: 'var(--blue-bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--blue)' }}>Progress</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--blue)' }}>{pct}%</div>
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <div className="progress-bar" style={{ height: '10px' }}>
                  <div className="progress-fill" style={{ width: `${pct}%`, background: color }}></div>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
                  {remaining > 0 ? `${formatCurrency(remaining)} remaining to reach goal` : <><PartyPopper size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Goal reached!</>}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button className="btn btn-gold btn-sm" onClick={() => openAddDonationModal(c)} disabled={loading || isSubmitting}>
                  <CreditCard size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Add Donation
                </button>
                <button className="btn btn-gold btn-sm" onClick={() => sendReminder(c)} disabled={loading || isSubmitting}>
                  <Send size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Send Reminder
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => sendProgressUpdate(c)} disabled={loading || isSubmitting || c.donors === 0}>
                  <BarChart3 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Update Donors
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => viewDonors(c)} disabled={loading}>
                  <Users size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Donors
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => openExportCampaignModal(c)} disabled={loading}>
                  <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => handleDuplicateCampaign(c)} disabled={loading || isSubmitting}>
                  <Copy size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Copy
                </button>
                {c.status === 'Active' && (
                  <button
                    className="btn btn-success btn-sm"
                    style={{ background: 'var(--green-bg)', color: 'var(--green)', border: '1px solid #a7d7be' }}
                    onClick={async () => {
                      try {
                        await dispatch(updateGivingCampaign({ id: c.id, data: { status: 'Completed' } })).unwrap();
                        await dispatch(getGivingCampaigns({ per_page: itemsPerPage, page: currentPage, search: searchTerm, status: statusFilter }));
                        await dispatch(getGivingCampaignStats());
                        showToastMessage(`✅ "${c.name}" marked as completed`, 'success');
                      } catch (err) {
                        showToastMessage(err.message || 'Failed to complete campaign', 'error');
                      }
                    }}
                    disabled={loading || isSubmitting}
                  >
                    <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Complete
                  </button>
                )}
              </div>
            </div>
          );
        })
      ) : (
        <div className="empty-state">
          <div className="empty-icon"><DollarSign size={32} strokeWidth={1.5} /></div>
          <div className="empty-title">No giving campaigns found</div>
          <div className="empty-text">Try adjusting your search or filters</div>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginTop: '20px', padding: '16px', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            Showing <strong>{((currentPageNum - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPageNum * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> campaigns
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
              <select className="form-select" style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }} value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} disabled={loading}>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={6}>6</option>
                <option value={10}>10</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button className="btn btn-ghost btn-sm" onClick={goToPreviousPage} disabled={currentPageNum === 1 || loading}>← Prev</button>
              {getPageNumbers().map(page => (
                <button key={page} className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`} onClick={() => goToPage(page)} disabled={loading}>{page}</button>
              ))}
              <button className="btn btn-ghost btn-sm" onClick={goToNextPage} disabled={currentPageNum === totalPages || loading}>Next →</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title"><NotebookPen size={16} style={{ verticalAlign: '-3px' }} /> Giving Reminder SMS Template</div>
        </div>
        <div className="grid-2">
          <div>
            <textarea className="form-textarea" rows="6" defaultValue={`Hello {FirstName}! 🙏 Our {CampaignName} campaign needs your support. We've raised GH₵ {Raised} of our GH₵ {Goal} goal.

📱 Mobile Money: MTN - 0244123456 (${organizationName})
🏦 Bank Transfer: GCB - 0123456789 (${organizationName})
💵 Cash: In-person at church office

Every gift counts! — ${organizationName}`} />
            <div className="form-hint">Merge fields: {'{FirstName}'}, {'{CampaignName}'}, {'{Raised}'}, {'{Goal}'} - {'{FirstName}'} will be replaced with each member's actual name</div>
          </div>
          <div style={{ background: 'var(--navy)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '11px', color: 'var(--gold)', marginBottom: '8px', fontWeight: '600' }}>{organizationName.toUpperCase()}</div>
            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,.85)', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
              Hello Emmanuel! 🙏 Our Building Fund Drive campaign needs your support. We've raised GH₵ 38,420 of our GH₵ 50,000 goal.

              📱 Mobile Money: MTN - 0244123456 ({organizationName})
              🏦 Bank Transfer: GCB - 0123456789 ({organizationName})
              💵 Cash: In-person at church office

              Every gift counts! — {organizationName}
            </div>
          </div>
        </div>
        <div style={{ marginTop: '14px', display: 'flex', gap: '10px' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => showToastMessage('💾 Template saved!', 'success')}>
            <Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Template
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .progress-bar {
          width: 100%;
          height: 6px;
          background: var(--cream);
          borderRadius: 4px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.5s ease;
        }
        .member-row-avatar {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          flex-shrink: 0;
        }
        .btn-primary {
          background: var(--gold);
          color: var(--navy);
          border: none;
        }
        .btn-primary:hover {
          background: var(--gold-dark);
        }
        .btn-sm {
          padding: 4px 10px;
          font-size: 12px;
        }
        .btn-icon {
          padding: 4px 6px;
        }
        .stat-change {
          font-size: 12px;
          font-weight: 600;
          margin-top: 2px;
        }
        .stat-change.up {
          color: var(--green);
        }
        .stat-change.down {
          color: var(--red);
        }
        .form-hint {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
        .grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        @media (max-width: 768px) {
          .grid-2 {
            grid-template-columns: 1fr;
          }
        }
        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }
      `}</style>
    </>
  );
};

export default GivingCampaigns;